import { useEffect, useMemo, useRef, useState } from "react";
import {
  FilePlus2, History, Plus, Settings, Package, Users, Shield, ChevronRight,
  LogOut, RefreshCw, Save, X, Link as LinkIcon, BarChart3, Package2,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";

import api, { readError } from "./api";
import { useAuth } from "./AuthContext";
import { defaultCompany, emptyQuote, emptyItem, currencyLabel } from "./studioUtils";
import { WhatsAppSendDialog } from "./components/WhatsAppSendDialog";
import PrintSheet from "./PrintSheet";
import QuoteEditor from "./views/QuoteEditor";
import QuoteHistory from "./views/QuoteHistory";
import ProductCatalog from "./views/ProductCatalog";
import CustomerList from "./views/CustomerList";
import CompanySettings from "./views/CompanySettings";
import AdminPanel from "./views/AdminPanel";
import Dashboard from "./views/Dashboard";
import QuoteTemplates from "./views/QuoteTemplates";

export default function Studio() {
  const { user, logout } = useAuth();
  const [view, setView] = useState("new");
  const [rate, setRate] = useState(45.9);
  const [rateInfo, setRateInfo] = useState({ source: "İş kuralı varsayılanı", stale: true });
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [productEditActive, setProductEditActive] = useState(false);
  const [company, setCompany] = useState(defaultCompany);
  const [catalog, setCatalog] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [savedRef, setSavedRef] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [savedEan13, setSavedEan13] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [revising, setRevising] = useState(false);
  const [revisionOf, setRevisionOf] = useState(null);
  const [primaryColor, setPrimaryColor] = useState(
    () => localStorage.getItem("app-primary-color") || "#2563eb"
  );
  const [quote, setQuote] = useState(emptyQuote());
  const [whatsappQuote, setWhatsappQuote] = useState(null);
  const currentCurrency = useRef(quote.currency);
  const rateRequest = useRef(0);
  currentCurrency.current = quote.currency;

  const totals = useMemo(() =>
    quote.items.reduce((acc, item) => {
      const unit = Number(item.list_price || 0) * (1 - Number(item.discount || 0) / 100);
      const total = Number(item.quantity || 0) * unit;
      return { usd: acc.usd + total, tl: acc.tl + total * rate };
    }, { usd: 0, tl: 0 }),
    [quote.items, rate]);
  const vat = totals.tl * Number(quote.vat_rate || 0) / 100;
  const publicUrl = savedRef ? `${window.location.origin}/quote/public/${savedRef}` : null;

  const applyPrimaryColor = (color) => {
    setPrimaryColor(color);
    localStorage.setItem("app-primary-color", color);
    document.documentElement.style.setProperty("--blue", color);
  };
  useEffect(() => { document.documentElement.style.setProperty("--blue", primaryColor); }, [primaryColor]);

  const selectedRate = (currency, info) => {
    if (currency === "TRY") return 1;
    if (currency === "USD") return Number(info.rate || 0);
    const raw = Number(info[currency === "EUR" ? "eur_rate" : "gbp_rate"] || 0);
    if (!raw) return 0;
    const value = raw + Number(company.rate_margin ?? 0.4);
    const step = Number(company.rate_rounding);
    return step ? Number((Math.round(value / step) * step).toFixed(2)) : value;
  };
  const changeCurrency = (currency) => {
    rateRequest.current += 1;
    currentCurrency.current = currency;
    setQuote(q => ({ ...q, currency, usd_rate: q.usd_rate || rateInfo.rate || 0,
      eur_rate: q.eur_rate || rateInfo.eur_rate || 0, gbp_rate: q.gbp_rate || rateInfo.gbp_rate || 0 }));
    setRate(selectedRate(currency, rateInfo));
  };
  const loadRate = async () => {
    const requestId = ++rateRequest.current;
    try {
      const { data } = await api.get("/quote/usd-try"); setRateInfo(data);
      if (requestId !== rateRequest.current) return;
      setRate(selectedRate(currentCurrency.current, data));
      setQuote(q => ({ ...q, usd_rate: data.rate, eur_rate: data.eur_rate, gbp_rate: data.gbp_rate }));
    }
    catch { toast.error("Kur alınamadı"); }
  };
  const loadHistory = async () => {
    try { const { data } = await api.get("/quotes", { params: { q: search, status: statusFilter } }); setHistory(data); }
    catch (err) { toast.error(readError(err, "Teklif geçmişi alınamadı")); }
  };
  const loadCompany = async () => {
    try {
      const { data } = await api.get(user?.role === "admin" ? "/settings/company" : "/company/profile");
      const merged = { ...defaultCompany, ...data };
      setCompany(merged);
      if (merged.primary_color && merged.primary_color !== primaryColor) applyPrimaryColor(merged.primary_color);
    } catch { toast.error("Şirket bilgileri alınamadı"); }
  };
  const loadCatalog = async () => {
    try { const { data } = await api.get("/products/all"); setCatalog(data); }
    catch (err) { toast.error(readError(err)); }
  };
  const loadCustomers = async () => {
    try { const { data } = await api.get("/customers"); setCustomers(data); }
    catch (err) { toast.error(readError(err)); }
  };

  useEffect(() => { loadRate(); loadCompany(); }, []);
  useEffect(() => {
    if (view === "history") loadHistory();
    // eslint-disable-next-line
  }, [statusFilter]);

  useEffect(() => {
    if (!productEditActive) { setSuggestions([]); return; }
    const item = quote.items[quote.items.length - 1];
    if (!item?.product_name) { setSuggestions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { q: item.product_name } });
        setSuggestions(data);
      } catch {}
    }, 180);
    return () => clearTimeout(timer);
  }, [quote.items, productEditActive]);

  const save = async () => {
    if (quote.customer_approval && !revising) {
      toast.error("Müşteri işlem gördüğü için değiştirilemez. Revize oluşturun.");
      return;
    }
    try {
      const payload = { ...quote, currency_rate: quote.currency === "TRY" ? 1 : rate };
      let data;
      if (revising) {
        ({ data } = await api.post(`/quotes/${revisionOf}/revise`, payload));
        toast.success(`Revize kaydedildi: ${data.ref_code}`);
      } else {
        ({ data } = await api.post("/quotes", payload));
        toast.success(`Teklif kaydedildi: ${data.ref_code}`);
      }
      setSavedRef(data.ref_code); setSavedAt(data.created_at); setSavedEan13(data.ean13);
      setQuote({ ...emptyQuote(), ...data }); setRate(data.currency_rate);
      setRevising(false); setRevisionOf(null);
    } catch (err) { toast.error(readError(err, "Teklif kaydedilemedi")); }
  };

  const saveCompany = async () => {
    try {
      const payload = { ...company, primary_color: primaryColor };
      const { data } = await api.put("/settings/company", payload);
      setCompany({ ...defaultCompany, ...data });
      toast.success("Şirket bilgileri kaydedildi");
    } catch (err) { toast.error(readError(err)); }
  };

  const openQuote = async (ref) => {
    rateRequest.current += 1;
    try {
      const { data } = await api.get(`/quotes/${ref}`);
      setQuote({ ...emptyQuote(), ...data });
      setRate(data.currency_rate);
      setSavedRef(data.ref_code); setSavedAt(data.created_at); setSavedEan13(data.ean13);
      setRevising(false); setRevisionOf(null);
      setSuggestions([]); setProductEditActive(false);
      setView("new");
    } catch (err) { toast.error(readError(err, "Teklif açılamadı")); }
  };
  const duplicateQuote = async (ref) => {
    rateRequest.current += 1;
    try {
      const { data } = await api.post(`/quotes/${ref}/duplicate`);
      toast.success(`Kopya oluşturuldu: ${data.ref_code}`);
      loadHistory();
      setQuote({ ...emptyQuote(), ...data }); setRate(data.currency_rate);
      setSavedRef(data.ref_code); setSavedAt(data.created_at); setSavedEan13(data.ean13);
      setRevising(false); setRevisionOf(null);
      setSuggestions([]); setProductEditActive(false);
      setView("new");
    } catch (err) { toast.error(readError(err)); }
  };
  const reviseQuote = async (ref) => {
    rateRequest.current += 1;
    try {
      const { data } = await api.get(`/quotes/${ref}`);
      setQuote({ ...emptyQuote(), ...data });
      setRate(data.currency_rate);
      setSavedRef(null); setSavedAt(null); setSavedEan13(null);
      setRevising(true); setRevisionOf(data.parent_ref || data.ref_code);
      setSuggestions([]); setProductEditActive(false);
      setView("new");
      toast.info("Revize modu: değişiklikleri yapıp kaydedin");
    } catch (err) { toast.error(readError(err)); }
  };
  const updateStatus = async (ref, status) => {
    try { await api.patch(`/quotes/${ref}/status`, { status }); loadHistory(); }
    catch (err) { toast.error(readError(err)); }
  };

  const deleteQuote = async (ref) => {
    if (!window.confirm(`${ref} numaralı teklifi kalıcı olarak silmek istediğinizden emin misiniz?`)) return;
    try {
      await api.delete(`/quotes/${ref}`);
      toast.success("Teklif silindi");
      loadHistory();
    } catch (err) { toast.error(readError(err, "Silme başarısız")); }
  };

  const sendToCustomer = async (ref) => {
    if (!window.confirm(`${ref} teklifini müşteriye e-posta olarak göndermek istiyor musunuz?`)) return;
    try {
      const { data } = await api.post(`/quotes/${ref}/send-to-customer`, { attach_pdf: true });
      toast.success(`Gönderildi: ${data.sent_to}`);
      loadHistory();
    } catch (err) { toast.error(readError(err, "Gönderim başarısız")); }
  };

  const downloadPdf = (ref) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/quotes/${ref}/pdf`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openWhatsApp = async (ref) => {
    try { const { data } = await api.get(`/quotes/${ref}`); setWhatsappQuote(data); }
    catch (err) { toast.error(readError(err, "Teklif açılamadı")); }
  };

  const createPaymentLink = async (ref) => {
    if (!ref) return;
    if (!window.confirm(`${ref} teklifi için ${company.payment_provider} üzerinden ödeme linki oluşturulsun mu?`)) return;
    try {
      const { data } = await api.post(`/quotes/${ref}/payment-link`, {});
      toast.success("Ödeme linki oluşturuldu");
      setQuote(q => ({ ...q, payment_link: data.url, payment_provider: data.provider }));
      loadHistory();
    } catch (err) { toast.error(readError(err, "Ödeme linki oluşturulamadı")); }
  };

  const applyTemplate = (tpl) => {
    setQuote(q => ({
      ...q,
      items: tpl.items.map(it => ({ ...it })),
      notes: tpl.default_notes || q.notes,
      delivery_terms: tpl.default_delivery_terms || q.delivery_terms,
      payment_terms: tpl.default_payment_terms || q.payment_terms,
    }));
    setSavedRef(null); setSavedAt(null); setSavedEan13(null);
    setRevising(false); setRevisionOf(null);
    setSuggestions([]); setProductEditActive(false);
    setView("new");
    toast.success(`"${tpl.name}" şablonu yeni tekiflere uygulandı`);
  };

  const newQuote = () => {
    rateRequest.current += 1;
    setRate(selectedRate("USD", rateInfo));
    setQuote(emptyQuote());
    setSavedRef(null); setSavedAt(null); setSavedEan13(null);
    setRevising(false); setRevisionOf(null);
    setSuggestions([]); setProductEditActive(false);
    setView("new");
  };

  const nav = (next) => {
    if ((next === "settings" || next === "admin") && user?.role !== "admin") return;
    setView(next);
    if (next === "history") loadHistory();
    if (next === "settings") loadCompany();
    if (next === "catalog") loadCatalog();
    if (next === "customers") loadCustomers();
    if (next === "admin") {} // AdminPanel loads itself
  };

  const copyPublicLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(
      () => toast.success("Link panoya kopyalandı"),
      () => toast.error("Kopyalanamadı")
    );
  };

  const isAdmin = user?.role === "admin";

  return (
    <>
      <div className="app-shell">
        <aside className="sidebar">
          <div className="brand">
            {company.logo_url ? (
              <img src={company.logo_url} alt="logo" className="brand-logo" data-testid="sidebar-logo" />
            ) : (
              <div className="brand-mark">{(company.company_name || "C")[0]}</div>
            )}
            <div>
              <strong data-testid="company-name-display">{company.company_name || "CDS Teknoloji"}</strong>
              <span>Teklif Stüdyosu</span>
            </div>
          </div>
          <NavBtn testId="new-quote-button" active={view === "new"} onClick={newQuote}
            icon={<FilePlus2 size={18} />} label="Yeni Teklif" />
          <NavBtn testId="dashboard-button" active={view === "dashboard"} onClick={() => nav("dashboard")}
            icon={<BarChart3 size={18} />} label="Kontrol Paneli" />
          <NavBtn testId="quote-history-button" active={view === "history"} onClick={() => nav("history")}
            icon={<History size={18} />} label="Teklif Geçmişi" />
          <NavBtn testId="templates-button" active={view === "templates"} onClick={() => nav("templates")}
            icon={<Package2 size={18} />} label="Şablonlar" />
          <NavBtn testId="customers-button" active={view === "customers"} onClick={() => nav("customers")}
            icon={<Users size={18} />} label="Cari Listesi" />
          <NavBtn testId="catalog-button" active={view === "catalog"} onClick={() => nav("catalog")}
            icon={<Package size={18} />} label="Ürün Kataloğu" />
          {isAdmin && <NavBtn testId="company-settings-button" active={view === "settings"} onClick={() => nav("settings")}
            icon={<Settings size={18} />} label="Ayarlar" />}
          {isAdmin && (
            <NavBtn testId="admin-button" active={view === "admin"} onClick={() => nav("admin")}
              icon={<Shield size={18} />} label="Kullanıcılar" />
          )}
          <div className="sidebar-foot">
            <div className="user-chip" data-testid="user-chip">
              <div>
                <b>{user?.name || user?.email}</b>
                <span>{user?.role === "admin" ? "Yönetici" : "Kullanıcı"}</span>
              </div>
              <button className="icon-button" title="Çıkış" onClick={logout} data-testid="logout-button">
                <LogOut size={15} />
              </button>
            </div>
          </div>
        </aside>
        <main className="main-content" data-view={view} data-testid="studio-main">
          <header className="topbar">
            <div>
              <p className="eyebrow">TEKLİF YÖNETİMİ / 2026</p>
              <h1>
                {view === "new" ? (revising ? "Revize teklif" : "Yeni teklif oluştur")
                  : view === "dashboard" ? "Kontrol paneli"
                  : view === "history" ? "Teklif geçmişi"
                  : view === "templates" ? "Şablonlar"
                  : view === "catalog" ? "Ürün kataloğu"
                  : view === "customers" ? "Cari listesi"
                  : view === "admin" ? "Kullanıcılar"
                  : "Ayarlar"}
              </h1>
            </div>
            <div className="top-actions">
              {view === "new" && (
                <>
                  <div className="rate-pill" data-testid="exchange-rate-display">
                    <span>{currencyLabel(quote.currency)} / TL</span><b>{rate.toFixed(2)} ₺</b>
                    <button data-testid="refresh-rate-button" onClick={loadRate} disabled={quote.currency === "TRY"} title="Kuru yenile"><RefreshCw size={14} /></button>
                  </div>
                  <button data-testid="save-quote-button-top" className="primary-button" onClick={save} disabled={!!quote.customer_approval && !revising}>
                    <Save size={16} /> {revising ? "Revize Kaydet" : (quote.customer_approval ? "Kilitli" : "Teklifi Kaydet")}
                  </button>
                </>
              )}
            </div>
          </header>

          {view === "new" && (
            <QuoteEditor
              quote={quote} setQuote={setQuote} rate={rate} setRate={setRate}
              rateInfo={rateInfo} loadRate={loadRate}
              onCurrencyChange={changeCurrency}
              suggestions={suggestions} setSuggestions={setSuggestions}
              onProductEdit={() => setProductEditActive(true)}
              save={save} totals={totals} vat={vat}
              savedRef={savedRef} savedAt={savedAt} ean13={savedEan13} publicUrl={publicUrl}
              revising={revising} revisionOf={revisionOf}
              locked={!!quote.customer_approval}
              onOpenQr={() => setQrOpen(true)} onCopyLink={copyPublicLink}
              onDownloadPdf={() => savedRef && downloadPdf(savedRef)}
              onSendToCustomer={() => savedRef && sendToCustomer(savedRef)}
              onSendWhatsApp={() => savedRef && openWhatsApp(savedRef)}
              onCreatePaymentLink={() => savedRef && createPaymentLink(savedRef)}
              customers={customers} loadCustomers={loadCustomers}
              company={company}
            />
          )}
          {view === "history" && (
            <QuoteHistory
              history={history} search={search} setSearch={setSearch} loadHistory={loadHistory}
              statusFilter={statusFilter} setStatusFilter={setStatusFilter}
              openQuote={openQuote} duplicateQuote={duplicateQuote} reviseQuote={reviseQuote}
              onEmptyNew={newQuote} updateStatus={updateStatus}
              isAdmin={isAdmin} deleteQuote={deleteQuote} sendToCustomer={sendToCustomer}
              downloadPdf={downloadPdf}
              sendWhatsApp={openWhatsApp}
            />
          )}
          {view === "dashboard" && <Dashboard />}
          {view === "templates" && <QuoteTemplates onApply={applyTemplate} />}
          {view === "catalog" && (
            <ProductCatalog catalog={catalog} loadCatalog={loadCatalog} />
          )}
          {view === "customers" && (
            <CustomerList customers={customers} loadCustomers={loadCustomers} />
          )}
          {view === "settings" && isAdmin && (
            <CompanySettings
              company={company} setCompany={setCompany}
              primaryColor={primaryColor} applyPrimaryColor={applyPrimaryColor}
              saveCompany={saveCompany} backTo={() => nav("new")}
              isAdmin={isAdmin}
            />
          )}
          {view === "admin" && isAdmin && <AdminPanel me={user} />}
        </main>

        {qrOpen && publicUrl && (
          <div className="modal-overlay" onClick={() => setQrOpen(false)}>
            <div className="modal-card" onClick={e => e.stopPropagation()} data-testid="qr-modal">
              <button className="modal-close" onClick={() => setQrOpen(false)} data-testid="qr-close"><X size={16} /></button>
              <h2>Müşteri Paylaşım Linki</h2>
              <p>Müşteriniz bu QR kodu telefonuyla tarayarak teklifi anlık görebilir.</p>
              <div className="qr-holder"><QRCodeCanvas value={publicUrl} size={220} level="M" includeMargin /></div>
              <div className="qr-link">
                <code data-testid="qr-link-text">{publicUrl}</code>
                <button className="primary-button" onClick={copyPublicLink}><LinkIcon size={14} /> Kopyala</button>
              </div>
              <a className="outline-button" href={publicUrl} target="_blank" rel="noreferrer" data-testid="qr-open-link">Linki aç</a>
            </div>
          </div>
        )}

        {whatsappQuote && <WhatsAppSendDialog quote={whatsappQuote} onClose={() => setWhatsappQuote(null)} />}
        <Toaster position="bottom-right" />
      </div>

      {view === "new" && (
        <PrintSheet
          quote={quote} company={company} rate={rate}
          totals={totals} vat={vat}
          refCode={savedRef} createdAt={savedAt}
          publicUrl={publicUrl} ean13={savedEan13}
          showImages={quote.show_images}
          showCustomerSign={true}
          showSecondPage={false}
        />
      )}
    </>
  );
}

function NavBtn({ testId, active, onClick, icon, label }) {
  return (
    <button data-testid={testId} className={`nav-item ${active ? "active" : ""}`} onClick={onClick}>
      {icon} {label} <ChevronRight size={15} />
    </button>
  );
}
